import { Alert, AlertDescription, Skeleton } from '@sitewise/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';


import { exchangeHandoff } from '../../api/auth.js';
import { ApiError } from '../../lib/api-client.js';

import { AuthShell } from './auth-shell.js';

export function HandoffPage() {
  const [params] = useSearchParams();
  // The super-admin dashboard places the handoff token in the query string
  // OR the URL hash (`#handoff=...`); both are accepted here so the route
  // works regardless of how the dashboard chose to redirect.
  const tokenFromQuery = params.get('handoff') ?? params.get('token');
  const tokenFromHash = (() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return null;
    const usp = new URLSearchParams(hash);
    return usp.get('handoff') ?? usp.get('token');
  })();
  const token = tokenFromQuery ?? tokenFromHash ?? '';
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: () => exchangeHandoff(token),
    onSuccess: () => {
      // wipe the hash so the token never lingers in browser history
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', window.location.pathname);
      }
      navigate('/dashboard', { replace: true });
    },
  });

  useEffect(() => {
    if (token) mutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <AuthShell title="Missing handoff token">
        <Alert variant="danger">
          <AlertDescription>
            This page requires a super-admin handoff token. Return to the platform console.
          </AlertDescription>
        </Alert>
      </AuthShell>
    );
  }

  if (mutation.error) {
    return (
      <AuthShell
        title="Handoff failed"
        footer={
          <Link to="/login" className="text-fg hover:underline">
            Back to sign in
          </Link>
        }
      >
        <Alert variant="danger">
          <AlertDescription>
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : 'The handoff token is invalid or expired.'}
          </AlertDescription>
        </Alert>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Connecting you to this organization…">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
    </AuthShell>
  );
}
